import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import { device } from '../../../utils/styled-component-mixins';
import ButtonLink from '../../shared/ButtonLinkComponent';
import AllianceLocationsLandingTypeAhead from './AllianceLocationsLandingTypeAhead';
import OfficeCard from '../Location/OfficeCard';
import ServerStylesConditionalWrapper from '../../shared/ServerStylesConditionalWrapper';
import { useLocalization } from '../../custom-hooks/useLocalization';
import useMediaQuery from '../../custom-hooks/useMediaQuery';
import SkeletonCard from '../cards/SkeletonCard';
import NoResultsCard from '../../shared/NoResultsCard';
import LocationsLandingSearchStyled from '../LocationsLanding/LocationsLandingSearchStyled';

const DesktopSkeletonCards = () => Array.from(Array(3), (_, i) => <SkeletonCard key={i} />);
const MobileSkeletonCards = () => (
  <Swiper
    className="office-slider"
    modules={[Pagination]}
    pagination={{ clickable: false }}
    spaceBetween={16}
    slidesPerView="auto"
    centeredSlides
  >
    {Array.from(Array(3), (_, i) => (
      <SwiperSlide key={i}>
        <SkeletonCard key={i} />
      </SwiperSlide>
    ))}
  </Swiper>
);

const AllianceLocationsLandingSearch = (props) => {
  const {
    heading,
    subHeading,
    searchResultsHeading,
    searchMethodConjunctionText,
    currentLocationButtonText,
    searchInputLabel,
    searchResultsDefaultLocationLabel
  } = props;

  const isDesktop = useMediaQuery(device.desktop);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const { resourceStrings } = useLocalization();

  const searchOffices = (latitude, longitude, location) => {
    setIsLoading(true);
    setSelectedLocation(location || null);
    fetch(`/api/alliance-search?searchstring=${latitude},${longitude}&range=150`)
      .then((response) => response.json())
      .then((data) => {
        const officesWithinFiftyMiles = data.filter((office) => office.distance <= 50);
        if (officesWithinFiftyMiles.length > 0) {
          setSearchResults(officesWithinFiftyMiles);
        } else {
          setSearchResults(data); // results returned from api withing 150 miles
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const getCurrentLocationSuccess = (pos) => {
    const { latitude, longitude } = pos.coords;
    setCurrentLocation({ lat: latitude, lon: longitude });
    searchOffices(latitude, longitude);
  };

  const getCurrentLocationError = (err) => {
    console.warn(`ERROR(${err.code}): ${err.message}`);
  };

  const getCurrentLocation = () => {
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    };

    if (currentLocation === null) {
      navigator.geolocation.getCurrentPosition(
        getCurrentLocationSuccess,
        getCurrentLocationError,
        options
      );
    } else {
      searchOffices(currentLocation.lat, currentLocation.lon);
    }
  };

  return (
    <ServerStylesConditionalWrapper>
      <LocationsLandingSearchStyled>
        <div className="container container-padding-default">
          <div className="location-search-heading">
            <h2>{heading}</h2>
            <div
              className="location-search-subheading"
              dangerouslySetInnerHTML={{ __html: subHeading }}
            />
          </div>
          <div className="location-search-input">
            <AllianceLocationsLandingTypeAhead
              id="alliance-typeahead"
              icon="search"
              searchInputLabel={searchInputLabel}
              onChangeFunction={searchOffices}
              typeAheadApi="/api/address/typeahead?query="
            />
            <div>
              <span>{searchMethodConjunctionText}</span>
              <ButtonLink
                type="plain-link"
                isButtonTag={true}
                text={currentLocationButtonText}
                theme="light"
                onClickFunction={() => getCurrentLocation()}
              />
            </div>
          </div>
        </div>
        {((searchResults !== null && searchResults.length !== 0) || isLoading) && (
          <>
            <div className="container container-padding-default">
              <h3>
                {searchResultsHeading}{' '}
                {selectedLocation !== null
                  ? selectedLocation?.address?.freeformAddress
                  : searchResultsDefaultLocationLabel}
              </h3>
            </div>
            {isDesktop ? (
              <div className="container container-padding-default">
                <ul className="non-bdo-style-list office-list">
                  {isLoading ? (
                    <DesktopSkeletonCards />
                  ) : (
                    searchResults.map((office, index) => (
                      <OfficeCard
                        url={office.website}
                        key={index}
                        name={office.officename}
                        cardThumbnail={office.cardthumbnail}
                        cardThumbnailAlt={office.cardthumbnailalt}
                        addressLine1={office.addressline1}
                        addressLine2={office.addressline2}
                        city={office.city}
                        state={office.state}
                        postalCode={office.postalcode}
                        phone={office.phone}
                        fax={office.fax}
                        resourceStrings={resourceStrings}
                        isAllianceLocation={true}
                        index={index}
                      />
                    ))
                  )}
                </ul>
              </div>
            ) : (
              <>
                {isLoading ? (
                  <MobileSkeletonCards />
                ) : (
                  <Swiper
                    className="office-slider"
                    modules={[Pagination]}
                    pagination={{ clickable: false }}
                    spaceBetween={16}
                    slidesPerView="auto"
                    centeredSlides
                  >
                    {isLoading ? (
                      <MobileSkeletonCards />
                    ) : (
                      searchResults.map((office, index) => (
                        <SwiperSlide key={index}>
                          <OfficeCard
                            url={office.website}
                            key={office.nodeid}
                            name={office.officename}
                            cardThumbnail={office.cardthumbnail}
                            cardThumbnailAlt={office.cardthumbnailalt}
                            addressLine1={office.addressline1}
                            addressLine2={office.addressline2}
                            city={office.city}
                            state={office.state}
                            postalCode={office.postalcode}
                            phone={office.phone}
                            fax={office.fax}
                            resourceStrings={resourceStrings}
                            isAllianceLocation={true}
                          />
                        </SwiperSlide>
                      ))
                    )}
                  </Swiper>
                )}
              </>
            )}
          </>
        )}
        {searchResults !== null && searchResults.length === 0 && !isLoading && (
          <NoResultsCard noResultsMessage={props.noResultsMessage} dangerouslySetInnerHTML={true} />
        )}
      </LocationsLandingSearchStyled>
    </ServerStylesConditionalWrapper>
  );
};

export default withLocalization(AllianceLocationsLandingSearch);
